import type { TemplateDefinition } from "./types";

export const T17: TemplateDefinition = {
  id: "t17",
  module: 5,
  number: 17,
  name: "Threat Assessment and Situational Awareness Response Protocol",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "Ambush attacks against law enforcement are preceded by predictable situational awareness failures. A certified officer operates from a documented threat assessment protocol — not instinct alone.",
  architectureContext:
    "This template serves the Tiered Access pillar. It converts the Color Code awareness system into a documented operational decision tool with pre-plans for high-risk locations — a professional competency that prevents ambush.",
  sections: [
    {
      id: "color_codes",
      title: "Section 1 — Color Code System as Operational Decision Tool",
      variant: "color-cards",
      fields: [
        { id: "white", type: "textarea", label: "WHITE — UNAWARE: Conditions in my assignment that produce White-level unawareness and how I eliminate them from my professional practice", placeholder: "Conditions producing unawareness and your elimination approach..." },
        { id: "yellow", type: "textarea", label: "YELLOW — RELAXED AWARENESS (Operational Baseline): How I maintain Yellow awareness for an 8-12 hour shift", placeholder: "How you sustain baseline awareness..." },
        { id: "yellow_practices", type: "textarea", label: "Physical and mental practices that sustain my baseline awareness", placeholder: "Your sustaining practices..." },
        { id: "orange_triggers", type: "textarea", label: "ORANGE — SPECIFIC ALERT: The triggers that move me to Orange in my patrol environment", placeholder: "Your Orange-level triggers..." },
        { id: "orange_actions", type: "textarea", label: "What I do at Orange level — my specific action steps", placeholder: "Your Orange-level actions..." },
        { id: "orange_doc", type: "textarea", label: "How I document my Orange-level assessment", placeholder: "Your documentation approach..." },
        { id: "red_indicators", type: "textarea", label: "RED — SPECIFIC THREAT: The indicators that move me to Red", placeholder: "Your Red-level indicators..." },
        { id: "red_decide", type: "textarea", label: "My decision to act — the threshold and documentation", placeholder: "Your decision threshold..." },
        { id: "red_post", type: "textarea", label: "Post-incident documentation protocol", placeholder: "Your post-incident protocol..." },
      ],
    },
    {
      id: "preplans",
      title: "Section 2 — High-Risk Location Pre-Plans",
      repeatable: {
        id: "high_risk_locations",
        label: "Pre-Plan",
        maxRows: 8,
        initialRows: 1,
        addLabel: "+ Add Location",
        fields: [
          { id: "name", type: "text", label: "Location Name", placeholder: "Location name..." },
          { id: "address", type: "text", label: "Address or GPS reference", placeholder: "Address..." },
          { id: "threats", type: "textarea", label: "Known threat indicators", placeholder: "Known threats..." },
          { id: "approach", type: "textarea", label: "Approach protocol", placeholder: "Your approach protocol..." },
          { id: "cover", type: "textarea", label: "Cover and concealment available", placeholder: "Available cover..." },
          { id: "backup", type: "textarea", label: "Backup protocol for this location", placeholder: "Your backup protocol..." },
          { id: "updated", type: "date", label: "Last updated" },
        ],
      },
    },
  ],
};

export const T18: TemplateDefinition = {
  id: "t18",
  module: 5,
  number: 18,
  name: "Personal Stress Management and Critical Incident Response Plan",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "The absence of a documented stress management plan is a career risk. Officers with documented plans are more likely to recognize warning signs and seek help before crisis point.",
  architectureContext:
    "This template serves the Value-Based Credentialing pillar. It makes stress management a documented, proactive system with critical incident response protocols — protecting the officer's career and wellbeing.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Daily Stress Inventory",
      fields: [
        { id: "stress_triggers", type: "textarea", label: "My personal occupational stress triggers (specific to my assignment and role)", placeholder: "Your specific stress triggers..." },
        { id: "stress_load", type: "slider", label: "My current daily stress load assessment (1-10)", min: 1, max: 10, step: 1 },
        { id: "stress_desc", type: "textarea", label: "Description of current stress load", placeholder: "Describe your current stress level..." },
        { id: "warning_indicators", type: "textarea", label: "Early warning indicators that my stress load is becoming unmanageable", placeholder: "Your warning signs..." },
      ],
    },
    {
      id: "s2",
      title: "Section 2 — Active Controls",
      repeatable: {
        id: "daily_controls",
        label: "Daily Control",
        maxRows: 8,
        initialRows: 1,
        addLabel: "+ Add Control",
        fields: [
          { id: "activity", type: "text", label: "Control Activity", placeholder: "e.g., Exercise, meditation, peer check-in" },
          { id: "frequency", type: "select", label: "Frequency", options: [{ label: "Daily", value: "daily" }, { label: "Several times weekly", value: "several_weekly" }, { label: "Weekly", value: "weekly" }] },
          { id: "time", type: "text", label: "Time required", placeholder: "e.g., 30 min" },
          { id: "evidence", type: "textarea", label: "Evidence that this works for me", placeholder: "Why this works for you..." },
        ],
      },
    },
    {
      id: "s3",
      title: "Section 3 — Critical Incident Response Protocol",
      description: "3-phase timeline:",
      variant: "timeline",
      fields: [
        { id: "first24_actions", type: "textarea", label: "FIRST 24 HOURS — My immediate post-incident actions", placeholder: "Your immediate actions..." },
        { id: "first24_contact", type: "textarea", label: "FIRST 24 HOURS — Who I contact in the first 24 hours", placeholder: "Who you contact..." },
        { id: "first24_avoid", type: "textarea", label: "FIRST 24 HOURS — What I avoid doing in the first 24 hours", placeholder: "What you avoid..." },
        { id: "h24_72_checkin", type: "textarea", label: "HOURS 24-72 — My check-in protocol at this phase", placeholder: "Your check-in protocol..." },
        { id: "h24_72_physical", type: "textarea", label: "HOURS 24-72 — Physical and sleep practices I maintain", placeholder: "Your physical/sleep practices..." },
        { id: "h24_72_support", type: "textarea", label: "HOURS 24-72 — Decision I make about professional support at this phase", placeholder: "Your support decision..." },
        { id: "d3_7_recovery", type: "textarea", label: "HOURS 72-168 (Days 3-7) — Recovery indicators I am monitoring", placeholder: "Your recovery indicators..." },
        { id: "d3_7_return", type: "textarea", label: "HOURS 72-168 (Days 3-7) — Return-to-full-duty assessment", placeholder: "Your return assessment..." },
      ],
    },
    {
      id: "s4",
      title: "Section 4 — Recovery Benchmarks",
      fields: [
        { id: "recovering_well", type: "textarea", label: "Measurable indicators that I am recovering well", placeholder: "Your recovery indicators..." },
        { id: "declining", type: "textarea", label: "Warning indicators that I am declining and need escalation", placeholder: "Your warning signs..." },
      ],
    },
    {
      id: "s5",
      title: "Section 5 — Escalation Plan",
      fields: [
        { id: "engage_point", type: "textarea", label: "The point at which I engage professional support", placeholder: "Your escalation threshold..." },
        { id: "primary_contact_name", type: "text", label: "Primary support contact — Name", placeholder: "Contact name..." },
        { id: "primary_contact_role", type: "text", label: "Primary support contact — Role", placeholder: "e.g., Peer Support Officer" },
        { id: "primary_contact_phone", type: "text", label: "Primary support contact — Phone", placeholder: "Phone number..." },
        { id: "no_decisions", type: "checkbox", label: "My commitment to not making major life decisions in the first 72 hours after a critical incident" },
      ],
    },
  ],
};

export const T19: TemplateDefinition = {
  id: "t19",
  module: 5,
  number: 19,
  name: "90-Day Officer Wellness Action Plan",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "Generic fitness plans are not built for rotating shifts, sleep disruption, and 12-hour foot patrols. This plan is.",
  architectureContext:
    "This template serves the Value-Based Credentialing pillar. It adapts wellness planning to the realities of shift work — covering fitness, sleep, nutrition, and recovery in a structured 90-day plan.",
  sections: [
    {
      id: "month1",
      title: "Week 1-4 (Month 1)",
      variant: "tabs",
      fields: [
        { id: "shift_rotation", type: "select", label: "My current shift rotation", options: [{ label: "Day", value: "day" }, { label: "Evening", value: "evening" }, { label: "Overnight", value: "overnight" }, { label: "Rotating", value: "rotating" }] },
        { id: "fitness_protocol", type: "textarea", label: "My fitness protocol adapted to my shift schedule", placeholder: "Your shift-adaptive fitness protocol..." },
        { id: "workout_timing", type: "textarea", label: "Workout timing relative to shift (pre-shift / post-shift / rest days)", placeholder: "Your workout timing..." },
        { id: "fitness_benchmark", type: "textarea", label: "My 90-day fitness benchmark", placeholder: "Your fitness benchmark..." },
      ],
    },
    {
      id: "month2",
      title: "Week 5-8 (Month 2)",
      variant: "tabs",
      fields: [
        { id: "sleep_schedule", type: "textarea", label: "My sleep schedule aligned to my shift rotation", placeholder: "Your sleep schedule..." },
        { id: "sleep_hygiene", type: "textarea", label: "Sleep hygiene practices I implement for shift-work disruption", placeholder: "Your sleep hygiene practices..." },
        { id: "shift_transition", type: "textarea", label: "How I manage shift rotation transitions and sleep", placeholder: "Your transition management..." },
        { id: "sleep_baseline_hours", type: "number", label: "My sleep quality baseline — hours", placeholder: "e.g., 6" },
        { id: "sleep_baseline_quality", type: "slider", label: "My sleep quality baseline — quality rating (1-10)", min: 1, max: 10, step: 1 },
      ],
    },
    {
      id: "month3",
      title: "Week 9-12 (Month 3)",
      variant: "tabs",
      fields: [
        { id: "meal_timing", type: "textarea", label: "My meal timing protocol for shift work", placeholder: "Your meal timing..." },
        { id: "nutrition_strategy", type: "textarea", label: "Nutritional strategy for sustained energy during long shifts", placeholder: "Your nutrition strategy..." },
        { id: "foods_avoid", type: "textarea", label: "Foods and habits I avoid before and during shift", placeholder: "What you avoid..." },
        { id: "decompression", type: "textarea", label: "My end-of-shift decompression routine", placeholder: "Your decompression routine..." },
        { id: "leave_work", type: "textarea", label: "Practices that help me leave work at work", placeholder: "Your practices..." },
        { id: "active_recovery", type: "textarea", label: "Active recovery practices on rest days", placeholder: "Your recovery practices..." },
      ],
    },
    {
      id: "benchmarks",
      title: "Section 5 — Annual Wellness Benchmarks",
      fields: [
        { id: "cardio_benchmark", type: "text", label: "My cardiovascular fitness benchmark", placeholder: "e.g., 1.5 mile run in 13:00" },
        { id: "strength_benchmark", type: "text", label: "My strength benchmark", placeholder: "e.g., Bench press body weight" },
        { id: "flex_benchmark", type: "text", label: "My flexibility/mobility benchmark", placeholder: "e.g., Touch toes, full shoulder ROM" },
        { id: "body_comp", type: "text", label: "My body composition benchmark", placeholder: "e.g., 15% body fat" },
        { id: "physical_date", type: "date", label: "My annual physical examination date" },
      ],
    },
  ],
};

export const T20: TemplateDefinition = {
  id: "t20",
  module: 5,
  number: 20,
  name: "Peer Support Resource Map and Help-Seeking Plan",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "Officers with documented support plans are statistically more likely to use them. If you need support today, you already know who to call and how.",
  architectureContext:
    "This template serves the Value-Based Credentialing pillar. It makes support resources visible, documented, and immediately accessible — converting help-seeking from a barrier into a pre-planned action.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Department Resources",
      repeatable: {
        id: "dept_resources",
        label: "Department Resource",
        maxRows: 10,
        initialRows: 1,
        addLabel: "+ Add Resource",
        fields: [
          { id: "type", type: "select", label: "Resource Type", options: [{ label: "Peer Support Officer", value: "peer_support" }, { label: "EAP", value: "eap" }, { label: "Chaplain", value: "chaplain" }, { label: "Mental Health Professional", value: "mhp" }, { label: "Union Rep", value: "union" }, { label: "Other", value: "other" }] },
          { id: "name", type: "text", label: "Name", placeholder: "Resource name..." },
          { id: "phone", type: "text", label: "Direct Phone", placeholder: "Phone..." },
          { id: "email", type: "text", label: "Email", placeholder: "Email..." },
          { id: "hours", type: "text", label: "Availability/Hours", placeholder: "e.g., 24/7, M-F 8-5" },
          { id: "confidential", type: "select", label: "Confidential?", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }, { label: "Partial", value: "partial" }] },
        ],
      },
    },
    {
      id: "s2",
      title: "Section 2 — External Resources",
      repeatable: {
        id: "ext_resources",
        label: "External Resource",
        maxRows: 10,
        initialRows: 4,
        prepopulated: [
          { name: "Safe Call Now", specialty: "First responder crisis line", contact: "1-206-459-3020", insurance: "N/A", confidential: "Yes" },
          { name: "First Responder Support Network", specialty: "Law enforcement trauma support", contact: "", insurance: "", confidential: "Yes" },
          { name: "Badge of Life", specialty: "LE psychological survival", contact: "", insurance: "", confidential: "Yes" },
          { name: "National Suicide Prevention Lifeline", specialty: "Crisis support", contact: "988", insurance: "N/A", confidential: "Yes" },
        ],
        fields: [
          { id: "name", type: "text", label: "Resource Name", placeholder: "Resource name..." },
          { id: "specialty", type: "text", label: "Specialty", placeholder: "e.g., Law enforcement trauma, PTSD" },
          { id: "contact", type: "text", label: "Contact", placeholder: "Phone or website..." },
          { id: "insurance", type: "text", label: "Insurance accepted", placeholder: "Insurance info..." },
          { id: "confidential", type: "select", label: "Confidential?", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }, { label: "Partial", value: "partial" }] },
        ],
      },
    },
    {
      id: "s3",
      title: "Section 3 — Personal Network",
      repeatable: {
        id: "personal_contacts",
        label: "Personal Contact",
        maxRows: 5,
        initialRows: 1,
        addLabel: "+ Add Contact",
        fields: [
          { id: "name", type: "text", label: "Name", placeholder: "Name..." },
          { id: "relationship", type: "text", label: "Relationship", placeholder: "e.g., Spouse, partner, trusted colleague" },
          { id: "phone", type: "text", label: "Phone", placeholder: "Phone..." },
          { id: "why", type: "text", label: "Why this person", placeholder: "Why you chose them..." },
        ],
      },
    },
    {
      id: "s4",
      title: "Section 4 — Help-Seeking Decision Framework",
      fields: [
        { id: "self_signs", type: "textarea", label: "I will seek support when I notice these signs in myself", placeholder: "Your personal warning signs..." },
        { id: "colleague_signs", type: "textarea", label: "I will seek support for a colleague when I notice these signs in them", placeholder: "Signs you watch for in colleagues..." },
        { id: "first_call", type: "text", label: "My first call is to", placeholder: "Name from Section 1..." },
        { id: "second_call", type: "text", label: "If that person is unavailable, my second call is to", placeholder: "Backup contact..." },
        { id: "timeframe", type: "text", label: "I will initiate help-seeking within [timeframe] of recognizing the need", placeholder: "e.g., 24 hours" },
      ],
    },
    {
      id: "s5",
      title: "Section 5 — Confidentiality Map",
      fields: [
        { id: "fully_confidential", type: "textarea", label: "Resources that are fully confidential (no reporting to supervisor or department)", placeholder: "Your confidential resources..." },
        { id: "mandatory_reporting", type: "textarea", label: "Resources that have mandatory reporting obligations I need to understand", placeholder: "Resources with reporting obligations..." },
        { id: "disclosure", type: "textarea", label: "What I can disclose and what I cannot disclose to my department when seeking support", placeholder: "Your disclosure understanding..." },
      ],
    },
  ],
};
